import { getAuth } from 'firebase/auth';
import config from 'config';
import axios from 'axios';

const auth = getAuth();
const apiUrl = config.apiUrl;

/**
 * Send feedback for a specific analysis block
 * @param {Object} feedbackData - The feedback data
 * @param {string} feedbackData.taskId - The task ID of the analysis report
 * @param {string} feedbackData.blockId - The unique identifier for the block
 * @param {string} feedbackData.blockType - The type of block (e.g., 'audience', 'differentiation', 'tone_of_voice')
 * @param {string} feedbackData.blockTitle - The title of the block
 * @param {string} feedbackData.blockContent - The content of the block
 * @param {boolean} feedbackData.isHelpful - Whether the user found the block helpful
 * @param {string} feedbackData.serviceName - The name of the service being analyzed
 * @param {string} feedbackData.userPlan - The user's plan (Free, Pro, etc.)
 * @returns {Promise} - A promise that resolves to the feedback submission result
 */
export const sendBlockFeedback = async (feedbackData) => {
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  try {
    const token = await user.getIdToken();
    
    // Format the request body according to the API specification
    const requestBody = {
      taskId: {
        task_id: feedbackData.taskId
      },
      blockId: feedbackData.blockId,
      blockType: feedbackData.blockType,
      blockTitle: feedbackData.blockTitle,
      blockContent: feedbackData.blockContent,
      isHelpful: feedbackData.isHelpful,
      serviceName: feedbackData.serviceName,
      userPlan: feedbackData.userPlan,
      userId: user.uid,
      reportType: "lp_analysis"
    };

    const response = await axios.post(
      `${apiUrl}/analysis_projects/save_report_feedback/`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    console.log('Feedback submitted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending feedback:', error);
    throw new Error(error.response?.data?.error || 'Failed to submit feedback');
  }
};

/**
 * Send feedback with actual API call (for production use) - DEPRECATED
 * @param {Object} feedbackData - The feedback data
 * @returns {Promise} - A promise that resolves to the feedback submission result
 */
export const sendBlockFeedbackToAPI = async (feedbackData) => {
  // This function is now deprecated - use sendBlockFeedback instead
  return sendBlockFeedback(feedbackData);
};

/**
 * Get feedback analytics for a specific task
 * @param {string} taskId - The task ID
 * @returns {Promise} - A promise that resolves to the feedback analytics
 */
export const getFeedbackAnalytics = async (taskId) => {
  // Mock analytics data - taskId will be used in production
  console.log('Fetching analytics for task:', taskId); // Use taskId to avoid ESLint warning
  
  return new Promise((resolve) => {
    setTimeout(() => {
      const mockAnalytics = {
        overallSatisfaction: 80,
        totalResponses: 50,
        satisfactionBreakdown: {
          helpful: 45,
          notHelpful: 5
        },
        blockBreakdown: {
          audience: { helpful: 8, notHelpful: 2 },
          differentiation: { helpful: 9, notHelpful: 1 },
          tone_of_voice: { helpful: 7, notHelpful: 3 },
          trust_factors: { helpful: 6, notHelpful: 4 },
          social_proof: { helpful: 5, notHelpful: 5 },
          pricing: { helpful: 4, notHelpful: 6 },
          cta: { helpful: 6, notHelpful: 4 }
        }
      };
      
      resolve(mockAnalytics);
    }, 300);
  });
}; 